import {
  CardPaymentMethodType,
  type CardRead,
  DefaultableCardType,
  PaymentProvider,
  PhysicalCardType,
} from "@/api-v3";
import { FALLBACK_CARD_TITLE } from "@/features/card";

/**
 * Route and DOM friendly slug derived from `card_type`. Kept only because
 * `/card/:cardType/transactions` and the details page keyed on it before
 * `card_type` existed; new code should branch on `card_type`. The
 * "expense-card" slugs name Highnote Spend Cards and stay as they are because
 * existing `/card/:cardType/...` links key on them.
 */
export type PaymentAccountCardType =
  | "virtual-card"
  | "physical-card"
  | "expense-card"
  | "physical-expense-card"
  | "virtual-stipend-card"
  | "physical-stipend-card"
  | "virtual-spend-card"
  | "physical-spend-card";

/** Consumer-facing card: every field present, provider and status normalised. */
export interface PaymentAccountCard {
  id: string;
  card_type: CardPaymentMethodType;
  type: PaymentAccountCardType;
  /** Lowercased, so it can be used directly as the `{provider}` path param. */
  provider: PaymentProvider;
  /** Uppercased provider status, e.g. "ACTIVE", "SUSPENDED", "ACTIVATION_REQUIRED". */
  status: string;
  currency: string;
  /** Card type label for Highnote, cardholder name for Stripe. Never branch on it. */
  name: string;
  /**
   * Localized display name from the backend, e.g. "Virtual stipend card", or
   * "Card" when it sends none. Unlike `name`, render it as the card heading.
   */
  title: string;
  /**
   * Decimal string, e.g. "0.00" — call Number() before formatting. `null` is a
   * Hidden Balance: PPS sends it for Spend Cards and read-only cards, and it
   * must never be rendered, not even as 0.00.
   */
  balance: string | null;
  is_read_only: boolean;
  /** ISO date string, "YYYY-MM-DD". */
  expiration_date: string;
  physical_order_status: string | null;
  /** Last 4 digits of the card. */
  card_number: string;
  /** Physical card shipping fields PPS may include; passed through untyped by PASS. */
  delivery_status?: string;
  tracking_number?: string;
  /** Stripe only: platform publishable key for Stripe.js. */
  publishable_key?: string;
  /** Stripe only: connected account the card was issued under. */
  account_id?: string;
  /** Stripe only: platform-wide address for raising a dispute or complaint. */
  dispute_email?: string;
}

export type PaymentAccountCardsResponse = PaymentAccountCard[];

const cardTypeToSlug: Record<CardPaymentMethodType, PaymentAccountCardType> = {
  [CardPaymentMethodType.vc]: "virtual-card",
  [CardPaymentMethodType.pc]: "physical-card",
  [CardPaymentMethodType.ec]: "expense-card",
  [CardPaymentMethodType.pec]: "physical-expense-card",
  [CardPaymentMethodType.vstc]: "virtual-stipend-card",
  [CardPaymentMethodType.pstc]: "physical-stipend-card",
  [CardPaymentMethodType.vspc]: "virtual-spend-card",
  [CardPaymentMethodType.pspc]: "physical-spend-card",
};

const isEnumValue = <T extends Record<string, string>>(
  enumObject: T,
  value: unknown
): value is T[keyof T] =>
  typeof value === "string" && Object.values(enumObject).includes(value);

/**
 * Normalises a raw `CardRead` into the shape the UI consumes. Returns `null`
 * for cards without a `card_type`, since nothing can be rendered or actioned
 * without knowing what kind of card it is.
 */
export const toPaymentAccountCard = (
  card: CardRead
): PaymentAccountCard | null => {
  if (!isEnumValue(CardPaymentMethodType, card.card_type)) {
    console.warn("Dropping card without a recognised card_type", card.id);
    return null;
  }

  const provider = (
    card.provider ?? PaymentProvider.highnote
  ).toLowerCase() as PaymentProvider;

  return {
    id: card.id,
    card_type: card.card_type,
    type: cardTypeToSlug[card.card_type],
    provider,
    status: (card.status ?? "").toUpperCase(),
    currency: card.currency ?? "",
    name: card.name ?? "",
    title: card.title || FALLBACK_CARD_TITLE,
    balance: card.balance == null ? null : String(card.balance),
    is_read_only: card.is_read_only ?? false,
    expiration_date: card.expiration_date ?? "",
    physical_order_status: card.physical_order_status ?? null,
    card_number: card.card_number ?? "",
    delivery_status: card.delivery_status as string | undefined,
    tracking_number: card.tracking_number as string | undefined,
    publishable_key: card.publishable_key ?? undefined,
    account_id: card.account_id ?? undefined,
    dispute_email: card.dispute_email ?? undefined,
  };
};

export const toPaymentAccountCards = (
  cards: CardRead[] | undefined
): PaymentAccountCard[] =>
  (cards ?? [])
    .map(toPaymentAccountCard)
    .filter((card): card is PaymentAccountCard => card !== null);

export const isVirtualCard = (card: PaymentAccountCard): boolean =>
  card.card_type === CardPaymentMethodType.vc;

export const isPhysicalCard = (card: PaymentAccountCard): boolean =>
  card.card_type === CardPaymentMethodType.pc;

/**
 * Spend Cards pay study costs from the sponsor's pool, never receive a payout
 * and can never be the Default Payment Account. Highnote issues them as ec/pec
 * and Stripe as vspc/pspc, and both are Spend Cards.
 */
export const isSpendCard = (card: PaymentAccountCard): boolean =>
  card.card_type === CardPaymentMethodType.ec ||
  card.card_type === CardPaymentMethodType.pec ||
  card.card_type === CardPaymentMethodType.vspc ||
  card.card_type === CardPaymentMethodType.pspc;

/** True when PASS attached a dispute contact, which it does for Stripe cards only. */
export const hasDisputeContact = (card: PaymentAccountCard): boolean =>
  Boolean(card.dispute_email);

/** Physical card types ship as plastic and are activated with their last4. */
export const canActivate = (
  cardType: CardPaymentMethodType
): cardType is PhysicalCardType => isEnumValue(PhysicalCardType, cardType);

/**
 * Card types that can become the Default Payment Account. Spend Cards never
 * receive a payout, so they are refused.
 */
export const canSetDefault = (
  cardType: CardPaymentMethodType
): cardType is DefaultableCardType =>
  isEnumValue(DefaultableCardType, cardType);
